import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { T } from "@freightsimple/ui-core/Typography/T";

const SUPPORT_EMAIL = "support@freightsimple.com";

interface UploadPhotosLoadFailedProps {
  /** The server says there is no shipment for this QR code, so retrying won't help. */
  notFound: boolean;
  /** Goes in the support email's subject so Ops can look the shipment up. */
  trackingId: string;
  onRetry: () => void;
}

function supportEmailHref(trackingId: string): string {
  const subject = `Photo upload: QR code not recognized (${trackingId})`;
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

/**
 * Shown when the page can't load the shipment the QR code points to. Without
 * it the shipper would watch the loading spinner forever.
 */
export function UploadPhotosLoadFailed(props: UploadPhotosLoadFailedProps) {
  return (
    <Stack gap={4} align="center">
      <Heading level={1} size="3xl" align="center">
        {props.notFound
          ? "We couldn't find this shipment"
          : "We couldn't load your shipment"}
      </Heading>
      <T color="secondary" align="center" testId="upload-photos-load-failed">
        {props.notFound ? (
          <>
            This QR code doesn't match a shipment we can take photos for. Please
            email{" "}
            <Link href={supportEmailHref(props.trackingId)}>
              {SUPPORT_EMAIL}
            </Link>
            .
          </>
        ) : (
          "Please check your connection and try again."
        )}
      </T>
      {!props.notFound && (
        <Button onClick={props.onRetry} data-testid="upload-photos-retry-load">
          Try again
        </Button>
      )}
    </Stack>
  );
}
